// @flow

import React, { Fragment } from "react";
import { localize } from "redux-i18n";
import { ButtonGroup, Button, ComboBox, type StepProps, View, Checkbox, Text } from "@components";
import type { AddNotificationData } from "@modules/shared/reducer/employee-notifications/types";
import type { EmployeeType } from "@modules/employee/types";
import NotificationTypeExposure from "../notification-type-exposure";
import { formatAuthoredBy } from "@transformers/names";
import { EMPLOYEE_REF_TYPES } from "@modules/shared/constants/notifications";

type OwnProps = $Exact<{
  data: $Exact<{
    notificationType: ?string,
  }>,
  notificationData: AddNotificationData,
  notificationTypes: Array<$Exact<{ title: string, const: string }>>,
  employeeToAdd: EmployeeType,
  recipient?: EmployeeType,
  isOpen: boolean,
  isCheckConfirmed: boolean,
  confirmationText?: string,
  employeeLookupPositions: Array<Object>,
}>;

type Props = $Exact<{
  ...StepProps,
  ...PropsFromLocalize,
  ...OwnProps,
}>;

export class Step2ConfigureNotification extends React.Component<Props> {
  componentDidMount() {
    if ((this.props.notificationData || {}).notificationType) {
      this.props.onChange(
        {
          notificationType: this.props.notificationData.notificationType,
        },
        true,
      );
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.editable && !prevProps.editable) {
      this.props.onChange(
        {
          notificationType: this.props.notificationTypes && this.props.notificationTypes[0].const,
        },
        true,
      );
    }
  }

  handleButtonClick = (type: string) => () => {
    this.props.onChange({
      notificationType: type,
    });
  };

  get employeeToEditPositions() {
    // edit
    if (this.props.notificationData) {
      if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT) {
        return this.recipientPositions || [];
      } else {
        return this.recipientPositions || [];
      }
    } else {
      if (this.props.employeeRefType === EMPLOYEE_REF_TYPES.RECIPIENT) {
        // add contact
        return this.recipientPositions || [];
      } else {
        // add employee
        return this.recipientPositions || [];
      }
    }
  }

  get employeeToAddPositions() {
    return ((this.props.employeeToAdd || {}).employmentSummary || {}).positions || [];
  }

  get recipientPositions() {
    const res = (
      this.props.notificationData?.recipientPositions ||
      this.props.employeeLookupPositions ||
      []
    ).map(p => ({
      id: p.id,
      jobTitle: p.jobTitle || p.id,
    }));

    return res;
  }

  get employeePositionsOptions() {
    const res = this.employeeToEditPositions.map(position => ({
      label: position.jobTitle || position.employeeReference || `${position.id}`,
      value: position.id,
    }));

    return res;
  }

  get employeePositionsSelected() {
    return ((this.props.notificationData || {}).employeePositions || []).map(position => ({
      value: position.id,
      label: position.jobTitle || position.employeeReference || position.id,
    }));
  }

  get selectedNotificationType() {
    return this.props.data?.notificationType;
  }

  handlePositionChange = (positionIds: Array<{ value: number }>) => {
    this.props.onChange({
      ...this.props.data,
      employeePositions: positionIds.map(position =>
        this.employeeToEditPositions.find(item => item.id === position),
      ),
    });
  };

  render() {
    if (!this.props.editable) {
      return null;
    }

    return (
      <View direction="column" justifyContent="center" alignItems="center" flex={1} gap={4}>
        <View padding="0.5rem 1rem">
          <Text className="message-box" text={this.props.t("NOTIFICATION_CONTACT_UPDATE_TEXT")} />
        </View>

        <div className="col-sm-8">
          <span className="pb-1">{this.props.t("NOTIFICATION_TYPE")}</span>
          <ButtonGroup className="mt-1 mb-4" fillSpace>
            {this.props.notificationTypes.map(type => (
              <Button
                key={type.const}
                color={this.props.data.notificationType === type.const ? "selected" : null}
                onClick={this.handleButtonClick(type.const)}
              >
                {this.props.t(type.const)}
              </Button>
            ))}
          </ButtonGroup>
          <NotificationTypeExposure currentType={this.selectedNotificationType} />
          {this.props.isOpen && (
            <Fragment>
              <span className="pb-1">{this.props.t("POSITION_SCOPE")}</span>
              <ComboBox
                className="mt-1 mb-4"
                isMulti
                options={this.employeePositionsOptions}
                onChange={this.handlePositionChange}
                defaultValue={this.employeePositionsSelected} // TODO - check the usage of this property, seems is not used in the combobox
                placeholder={this.props.t("ALL_POSITIONS")}
              />
            </Fragment>
          )}

          <View direction="row" gap={0} alignItems="start" justifyContent="space-between">
            <p
              dangerouslySetInnerHTML={{
                __html: this.props.t(this.props.confirmationText, {
                  notificationType: this.props.t(this.props.data.notificationType),
                  recipientName: formatAuthoredBy(this.props.recipient),
                }),
              }}
            />

            <Checkbox
              id="confirmation"
              checked={this.props.isCheckConfirmed}
              onChange={this.props.handleCheckConfirm}
            />
          </View>
        </div>
      </View>
    );
  }
}

export default localize()(Step2ConfigureNotification);
